/* Mixed-flavor choices enhance the native form. Prices start with live Shopify allocations. */
if (!customElements.get('jive-protein-bundle')) {
  customElements.define('jive-protein-bundle', class extends HTMLElement {
    initialize(host) {
      this.host = host;
      this.config = JSON.parse(this.querySelector('[data-bundle-config]').textContent);
      this.labels = JSON.parse(this.querySelector('[data-bundle-labels]').textContent);
      const initial = host.data.variants.find((variant) => String(variant.id) === host.variantInput.value) || host.data.variants.find((variant) => variant.available);
      this.size = initial.options[1];
      this.quantities = new Map(initial.available ? [[initial.options[0], 1]] : []);
      this.lastFlavor = initial.options[0];
      this.manualPlan = '';
      const options = { signal: host.controller.signal };
      this.querySelectorAll('[name="bundle_size"]').forEach((input) => { input.checked = input.value === this.size; });
      this.addEventListener('change', (event) => {
        if (event.target.name !== 'bundle_size') return;
        this.size = event.target.value;
        this.manualPlan = '';
        const removed = [];
        for (const [flavor] of this.quantities) {
          if (!this.variantFor(flavor)?.available) { this.quantities.delete(flavor); removed.push(flavor); }
        }
        if (removed.length) this.removedNotice = this.text('removed', { flavors: removed.join(', ') });
      }, options);
      host.form.addEventListener('change', (event) => {
        if (event.target === host.planInput) this.manualPlan = event.target.value;
      }, options);
      this.addEventListener('click', (event) => {
        const button = event.target.closest('[data-bundle-change]');
        if (!button || button.disabled) return;
        const flavor = button.closest('[data-bundle-flavor]').dataset.bundleFlavor;
        const variant = this.variantFor(flavor);
        if (!variant?.available) return;
        const quantity = Math.min(99, Math.max(0, (this.quantities.get(flavor) || 0) + Number(button.dataset.bundleChange)));
        if (quantity) this.quantities.set(flavor, quantity); else this.quantities.delete(flavor);
        this.lastFlavor = flavor;
        this.removedNotice = '';
        host.update();
      }, options);
      this.hidden = false;
    }
    text(key, values = {}) { return Object.entries(values).reduce((copy, [name, value]) => copy.replaceAll(`[${name}]`, String(value)), this.labels[key] || ''); }
    variantFor(flavor) { return this.host.data.variants.find((variant) => variant.options[0] === flavor && variant.options[1] === this.size); }
    selected() { return [...this.quantities].map(([flavor, quantity]) => ({ variant: this.variantFor(flavor), quantity })).filter((line) => line.quantity > 0 && line.variant?.available); }
    planFor(quantity) {
      const weeks = parseInt(this.size, 10) === 25 ? (quantity <= 1 ? 4 : quantity === 2 ? 8 : 12) : (quantity <= 2 ? 4 : quantity === 3 ? 6 : 8);
      return String(this.config.cadences[weeks]);
    }
    prices(quantity, subscription, lines = null) {
      const host = this.host;
      const tier = Math.min(4, Math.max(1, quantity)) - 1;
      const sizeRules = this.config.tiers[parseInt(this.size, 10)] || {};
      const rule = subscription ? sizeRules.subscriptionPercent?.[tier] : sizeRules.once?.[tier];
      const source = lines?.length ? lines : [{ variant: host.variant, quantity: Math.max(1, quantity) }];
      let total = 0, original = 0, native = 0;
      for (const line of source) {
        const base = line.variant?.price || 0;
        const allocation = line.variant?.selling_plan_allocations?.find((entry) => String(entry.selling_plan_id) === host.planInput?.value);
        const price = subscription ? allocation?.price ?? base : base;
        const discounted = rule == null || subscription ? price : price - Math.floor(price * rule / 100);
        original += base * line.quantity;
        native += price * line.quantity;
        total += subscription && rule != null && quantity > 1 ? Math.min(price * line.quantity, Math.round(base * line.quantity * (1 - rule / 100))) : discounted * line.quantity;
      }
      // Shopify targets each subscription variant line's original total, then rounds
      // its fixed allocation. A mixed bundle can differ by a cent from one flavor.
      return { total, original, native, known: rule != null, additional: native - total };
    }
    update(changeImage = true) {
      const host = this.host;
      const lines = this.selected();
      const quantity = lines.reduce((sum, line) => sum + line.quantity, 0);
      host.quantityInput.value = quantity || 1;
      host.variant = this.variantFor(this.lastFlavor) || lines[0]?.variant || host.data.variants.find((variant) => variant.options[1] === this.size && variant.available);
      const variant = host.variant;
      if (!variant) return;
      host.variantInput.value = variant.id;
      const candidates = lines.length ? lines.map((line) => line.variant) : [variant];
      const allocations = (candidates[0]?.selling_plan_allocations || []).filter((allocation) => candidates.every((entry) => entry.selling_plan_allocations?.some((plan) => plan.selling_plan_id === allocation.selling_plan_id)));
      const plans = host.data.plans.flatMap((group) => group.selling_plans);
      if (host.planInput) {
        const preferred = this.manualPlan || this.planFor(quantity);
        host.planInput.replaceChildren(...allocations.map((allocation) => {
          const plan = plans.find((entry) => entry.id === allocation.selling_plan_id);
          return new Option(this.text('every', { cadence: plan?.name || '' }), allocation.selling_plan_id);
        }));
        if (allocations.some((entry) => String(entry.selling_plan_id) === preferred)) host.planInput.value = preferred;
        else this.manualPlan = '';
      }
      const subChoice = host.form.querySelector('[name="purchase_option"][value="subscription"]');
      if (subChoice) {
        subChoice.disabled = allocations.length === 0;
        if (subChoice.checked && subChoice.disabled) host.form.querySelector('[value="once"]').checked = true;
      }
      host.subscription = host.form.querySelector('[name="purchase_option"]:checked')?.value === 'subscription';
      host.allocation = host.subscription ? allocations.find((entry) => String(entry.selling_plan_id) === host.planInput?.value) : null;
      host.offerEligible = false;
      if (host.planInput) host.planInput.disabled = !host.subscription;
      const once = this.prices(quantity, false, lines);
      const sub = this.prices(quantity, true, lines);
      this.price = host.subscription ? sub : once;
      host.total = quantity ? this.price.total : 0;
      host.querySelector('[data-once-price]').textContent = host.format(quantity ? once.total : 0);
      host.querySelector('[data-subscription-price]').textContent = host.format(quantity ? sub.total : 0);
      const subscriptionNote = subChoice?.closest('label')?.querySelector('small');
      if (subscriptionNote) subscriptionNote.textContent = this.text('first_saving', { percent: Math.round((1 - sub.total / sub.original) * 100) });
      const cadence = host.querySelector('[data-cadence]');
      if (cadence) cadence.hidden = !host.subscription;
      const plan = plans.find((entry) => entry.id === host.allocation?.selling_plan_id);
      if (host.subscription && plan) {
        const recurring = lines.reduce((sum, line) => {
          const allocation = line.variant.selling_plan_allocations.find((entry) => entry.selling_plan_id === plan.id);
          return sum + (allocation.price_adjustments?.at(-1)?.price ?? allocation.price) * line.quantity;
        }, 0);
        host.querySelector('[data-recurring-terms]').textContent = this.text(sub.additional ? 'recurring_then' : 'recurring', { price: host.format(recurring), cadence: plan.name });
      }
      host.querySelector('[data-total-price]').textContent = host.format(host.total);
      host.querySelector('[data-sticky-price]')?.replaceChildren(document.createTextNode(host.format(host.total)));
      host.querySelector('[data-heading-price]').textContent = host.format(variant.price);
      host.querySelector('[data-heading-size]').textContent = this.size;
      host.querySelector('[data-sticky-summary]')?.replaceChildren(document.createTextNode(quantity ? `${quantity} ${this.text(quantity === 1 ? 'bag' : 'bags')} · ${this.size} · ${host.subscription ? this.text('every', { cadence: plan?.name || '' }) : host.data.labels.once}` : this.text('choose_flavors')));
      host.submitButton.disabled = quantity === 0 || (host.subscription && !host.allocation) || host.busy;
      host.querySelector('[data-add-label]').textContent = host.busy ? host.data.labels.adding : quantity === 0 ? this.text('choose_flavors') : this.text('add_bundle');
      const promo = host.querySelector('.jive-product-main__promo');
      if (promo) promo.textContent = this.price.known ? (host.subscription && this.price.additional ? this.text('first_offer') : host.data.labels.tax) : this.text('before_offer');
      this.querySelector('[data-bundle-count]').textContent = `${quantity} ${this.text(quantity === 1 ? 'bag' : 'bags')}`;
      this.querySelectorAll('[data-bundle-flavor]').forEach((row) => {
        const flavor = row.dataset.bundleFlavor;
        const candidate = this.variantFor(flavor);
        const count = this.quantities.get(flavor) || 0;
        row.querySelector('[data-bundle-quantity]').textContent = count;
        row.querySelector('[data-bundle-availability]').textContent = candidate?.available ? '' : host.data.labels.soldOut;
        row.querySelector('[data-bundle-change="-1"]').disabled = !count || host.busy;
        row.querySelector('[data-bundle-change="1"]').disabled = !candidate?.available || count >= 99 || host.busy;
      });
      this.querySelectorAll('[data-bundle-tier]').forEach((node) => {
        const tier = Number(node.dataset.bundleTier);
        const preview = this.prices(tier, host.subscription);
        node.querySelector('[data-bundle-tier-price]').textContent = this.text('each', { price: host.format(preview.total / tier) });
        node.querySelector('[data-bundle-tier-savings]').textContent = !preview.known ? this.text('before_offers') : preview.original > preview.total ? this.text('save_percent', { percent: Math.round((1 - preview.total / preview.original) * 100) }) : this.text('one_bag');
        if (quantity && Math.min(quantity, 4) === tier) node.setAttribute('aria-current', 'step'); else node.removeAttribute('aria-current');
      });
      const saving = this.querySelector('[data-bundle-saving]');
      const saved = quantity ? this.price.original - this.price.total : 0;
      const perServing = quantity ? this.text('per_serving', { price: host.format(host.total / (quantity * parseInt(this.size, 10))) }) : '';
      saving.textContent = this.removedNotice || perServing + (saved ? this.text(this.price.known ? 'order_saving' : 'subscription_saving', { saved: host.format(saved) }) : quantity === 1 ? this.text('second_bag') : this.text('mix_any'));
      const image = host.data.flavors.find((flavor) => flavor.name === this.lastFlavor);
      if (changeImage && image?.image && host.lastVariant !== variant.id) host.showImage(image.image, image.name);
      host.lastVariant = variant.id;
    }
    getItems() {
      return this.selected().map((line) => ({ id: line.variant.id, quantity: line.quantity, ...(this.host.subscription ? { selling_plan: this.host.allocation.selling_plan_id } : {}) }));
    }
    chooseFlavor(flavor) {
      const variant = this.variantFor(flavor);
      if (!variant?.available) return false;
      if (!this.quantities.has(flavor)) this.quantities.set(flavor, 1);
      this.lastFlavor = flavor;
      this.host.update();
      this.querySelector(`[data-bundle-flavor="${CSS.escape(flavor)}"] [data-bundle-change="1"]`)?.focus({ preventScroll: true });
      return true;
    }
  });
}
